import React, { useMemo, useState } from 'react';
import MovieCard from '../components/MovieCard';
import MovieUploadModal from '../components/MovieUploadModal';
import { useMovies, useTrips, updateMovie } from '../data/firestore';
import { useAuth } from '../AuthContext';

const toMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const n = new Date(ts).getTime();
  return Number.isNaN(n) ? 0 : n;
};

// Unique trip ids a movie draws clips from.
export function movieTripIds(movie) {
  const ids = new Set();
  (movie.clips || []).forEach((c) => c.tripId && ids.add(c.tripId));
  return ids;
}

// When this movie last finished a bake (0 = never published).
export const bakeMs = (m) =>
  m.render?.status === 'ready' && m.render?.finishedAt ? m.render.finishedAt : 0;
// Ordering timestamp: bake date when published, else creation date.
const orderMs = (m) => bakeMs(m) || toMillis(m.createdAt);

// A movie family: the root + every "save as new version" branch (linked by
// lineage.rootId). The list shows ONE card per family — the most recently
// baked/edited version — with a badge that expands the rest.
function MovieFamily({ family }) {
  const [open, setOpen] = useState(false);
  const tag = (m) => (m.lineage ? (m.lineage.name || 'branch') : 'root');
  const pillBtn = (label, title, onClick, on = false) => (
    <button
      className={`cs-caption-pill${on ? ' on' : ''}`}
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {label}
    </button>
  );
  const versionPills = (m) => (
    <>
      <span className="cs-caption-pill">⎇ {tag(m)}</span>
      {pillBtn('⇱ own card', 'Pin: show this version as its own card in the list', () => updateMovie(m.id, { listStandalone: true }).catch(() => {}))}
    </>
  );
  if (family.count === 1) {
    const m = family.rep;
    // A standalone-opted version keeps its label + an unpin back to the group.
    if (m.listStandalone && m.lineage) {
      return (
        <MovieCard movie={m} captionExtra={
          pillBtn(`⎇ ${tag(m)} ⤸`, 'Unpin: fold this version back into its family card', () => updateMovie(m.id, { listStandalone: false }).catch(() => {}))
        } />
      );
    }
    return <MovieCard movie={m} />;
  }
  const rest = family.members.filter((m) => m.id !== family.rep.id);
  return (
    <>
      <MovieCard movie={family.rep} captionExtra={
        <>
          {pillBtn(`⎇ ${family.count} versions ${open ? '▴' : '▾'}`, open ? 'Collapse versions' : 'Show all versions', () => setOpen((v) => !v), true)}
          {open && versionPills(family.rep)}
        </>
      } />
      {open && rest.map((m) => <MovieCard key={m.id} movie={m} captionExtra={versionPills(m)} />)}
    </>
  );
}

const dayLabel = (ms) => {
  const d = new Date(ms);
  const today = new Date();
  const yest = new Date(today.getTime() - 86400000);
  const same = (a, b) => a.toDateString() === b.toDateString();
  const name = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
  if (same(d, today)) return `Today — ${name}`;
  if (same(d, yest)) return `Yesterday — ${name}`;
  return name;
};

export default function Movies() {
  const { user } = useAuth();
  const { docs: movies, error } = useMovies();
  const { docs: trips } = useTrips();
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState('all'); // 'all' | 'trip'
  const [sort, setSort] = useState('newest');

  // Collapse version families (lineage.rootId) to one entry each, fronted by
  // the most recently baked/edited member; the card badge expands the rest.
  // A member with listStandalone opts out of its group and gets its own card.
  const families = useMemo(() => {
    const byRoot = new Map();
    (movies || []).forEach((m) => {
      const key = m.listStandalone ? `solo:${m.id}` : (m.lineage?.rootId || m.id);
      if (!byRoot.has(key)) byRoot.set(key, []);
      byRoot.get(key).push(m);
    });
    return [...byRoot.values()].map((members) => {
      members.sort((a, b) => orderMs(b) - orderMs(a));
      return { rep: members[0], members, count: members.length };
    });
  }, [movies]);

  // Order by BAKE date (falling back to created date for unbaked drafts) —
  // after a day of iterating bakes, "which bake is this" is the question the
  // page must answer, so the All view also splits into one section per day.
  const sorted = useMemo(() => {
    const list = [...families];
    list.sort((a, b) => {
      const da = orderMs(a.rep);
      const db = orderMs(b.rep);
      return sort === 'oldest' ? da - db : db - da;
    });
    return list;
  }, [families, sort]);

  const byDay = useMemo(() => {
    const days = [];
    let cur = null;
    for (const f of sorted) {
      const ms = orderMs(f.rep);
      const key = ms ? new Date(ms).toDateString() : 'unknown';
      if (!cur || cur.key !== key) {
        cur = { key, label: ms ? dayLabel(ms) : 'No date', families: [] };
        days.push(cur);
      }
      cur.families.push(f);
    }
    return days;
  }, [sorted]);

  const byTrip = useMemo(() => {
    const groups = (trips || []).map((t) => ({
      trip: t,
      families: sorted.filter((f) => movieTripIds(f.rep).has(t.id)),
    }));
    const orphans = sorted.filter((f) => movieTripIds(f.rep).size === 0);
    return { groups: groups.filter((g) => g.families.length), orphans };
  }, [sorted, trips]);

  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <h1>Movies</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="cs-segment">
            <button className={view === 'all' ? 'on' : ''} onClick={() => setView('all')}>
              All
            </button>
            <button className={view === 'trip' ? 'on' : ''} onClick={() => setView('trip')}>
              By trip
            </button>
          </div>
          <select className="cs-input cs-input-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          {user && (
            <button className="cs-btn cs-btn-primary" onClick={() => setAdding(true)}>
              + Upload movie
            </button>
          )}
        </div>
      </div>

      {error && <div className="cs-empty" style={{ marginBottom: 16 }}>{error}</div>}

      {movies === null ? (
        <div>Loading…</div>
      ) : movies.length === 0 ? (
        <div className="cs-empty">
          No movies yet. Upload your first edited surf film, or build one in the
          editor from your trip footage.
        </div>
      ) : view === 'all' ? (
        <div>
          {byDay.map((day) => (
            <section key={day.key} style={{ marginBottom: 34 }}>
              <h2 style={{ fontSize: 17, margin: '0 0 2px', color: 'var(--text)' }}>{day.label}</h2>
              <div className="cs-help" style={{ fontSize: 12, marginBottom: 10 }}>
                {day.families.length} movie{day.families.length === 1 ? '' : 's'}
              </div>
              <div className="cs-movie-grid">
                {day.families.map((f) => (
                  <MovieFamily key={f.rep.id} family={f} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div>
          {byTrip.groups.map(({ trip, families: list }) => (
            <section key={trip.id} style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 19, marginBottom: 12 }}>{trip.name}</h2>
              <div className="cs-movie-grid">
                {list.map((f) => (
                  <MovieFamily key={f.rep.id} family={f} />
                ))}
              </div>
            </section>
          ))}
          {byTrip.orphans.length > 0 && (
            <section style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 19, marginBottom: 12 }}>Not linked to a trip</h2>
              <div className="cs-movie-grid">
                {byTrip.orphans.map((f) => (
                  <MovieFamily key={f.rep.id} family={f} />
                ))}
              </div>
            </section>
          )}
          {byTrip.groups.length === 0 && byTrip.orphans.length === 0 && (
            <div className="cs-empty">No movies to group.</div>
          )}
        </div>
      )}

      {adding && <MovieUploadModal onClose={() => setAdding(false)} />}
    </div>
  );
}
